import React, {useContext} from 'react';
import {ThemeContext} from 'styled-components';
import {useComponent} from '../../component-library';
import {useFeatureAppId} from '../../utils/use-feature-app-id';
import {useLayer} from '../../utils/use-layer';
import {Layer} from '../layer';
import {LayerPortal} from '../layer-portal';
import {
  BffEfficiencyResult,
  EfficiencyTranslations,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {EfficiencyLayerContent} from './efficiency-layer-content';
import {useTrackingFunctions} from '../../contexts/tracking-context';
import {TrackingEvent} from '../../types';

interface EfficiencyLayerButtonProps {
  disclaimerReferenceLabelTranslation: string | undefined;
  efficiencyTranslations: EfficiencyTranslations;
  efficiencyData: BffEfficiencyResult;
  modelName: string;
  closeButtonLabel: string;
  includeDisclaimerInEfficiencyLayer: boolean;
}

export const EfficiencyLayerButton = ({
  efficiencyData,
  efficiencyTranslations,
  disclaimerReferenceLabelTranslation,
  modelName,
  closeButtonLabel,
  includeDisclaimerInEfficiencyLayer,
}: EfficiencyLayerButtonProps): JSX.Element | null => {
  const {
    layerHeadline,
    layerCloseButtonAriaLabel,
    showAllValuesButtonAriaLabel,
    showAllValuesButtonLabel,
  } = efficiencyTranslations;
  const featureAppId = useFeatureAppId();

  const layerResult = useLayer({
    type: 'focus',
    layerId: `${featureAppId}_efficiency-data`,
    closeButtonLabel: layerCloseButtonAriaLabel,
    disableReturnFocus: false,
    userCloseable: true,
    usesFooterDisclaimers: true,
  });

  const tracking = useTrackingFunctions();

  const openLayer = () => {
    if (!layerResult.isOpen) {
      layerResult.open();
      tracking[TrackingEvent.ModelEfficiencyDataLayerOpen]?.({
        label: 'consumption-emission-details',
        link: {
          name: 'consumption-emission-details',
        },
      });
    }
  };

  const Text = useComponent('Text');
  const Button = useComponent('Button');
  const themeContext = useContext(ThemeContext);

  if (!efficiencyData || !efficiencyData.measurementStandardEfficiencyDetails) {
    return null;
  }

  return (
    <>
      <LayerPortal layerResult={layerResult}>
        <Layer headline={layerHeadline} theme={themeContext}>
          <EfficiencyLayerContent
            measurementStandardEfficiencyDetails={
              efficiencyData.measurementStandardEfficiencyDetails
            }
            modelName={modelName}
            disclaimerReferenceLabelTranslation={
              disclaimerReferenceLabelTranslation
            }
            efficiencyTranslations={efficiencyTranslations}
            closeButtonLabel={closeButtonLabel}
            includeDisclaimerInEfficiencyLayer={
              includeDisclaimerInEfficiencyLayer
            }
          />
        </Layer>
      </LayerPortal>
      <Button
        emphasis="text"
        onClick={openLayer}
        ariaLabel={showAllValuesButtonAriaLabel}
      >
        <Text tag="span" variant="copy-medium">
          {showAllValuesButtonLabel}
        </Text>
      </Button>
    </>
  );
};
